import styles from "./ParameterEnum.module.css";
import * as Menu from "src/components/ui-components/MenuPanel/MenuPanel";
import { InputField } from "src/components/ui-components/Input/InputField";
import { IconMagnifyingGlass } from "src/components/ui-components/Icons/IconMagnifyingGlass";
import { TabList } from "src/components/ui-components/Tabs/TabList";
import { Tab } from "src/components/ui-components/Tabs/Tab";
import { List as VirtualizedList } from "react-virtualized";
import { MenuSectionFreeformOptions } from "./MenuSectionFreeformOptions";

import { useState, useMemo } from "react";
import { useParameterEnumOptions } from "src/hooks/useParameterEnumOptions";
import { remToPx } from "src/utils/remToPx";

import type { ReactNode, CSSProperties } from "react";
import type { Parameter } from "src/utils/parameters/types";
import type { FormFieldProps } from "src/components/ui-components/Input/FormField";

type EnumOption = Exclude<Parameter["enumOptions"], undefined>[number];

interface Props {
  parameter: Parameter;
  parameterValues: Exclude<Parameter["values"], undefined>;
  onChange: (parameter: Parameter) => void;

  renderFormField: (props: {
    renderButton: FormFieldProps["renderInput"];
    disabled: boolean;
  }) => ReactNode;
}

export function ParamMenu(props: Props) {
  const enumOptions = useParameterEnumOptions(props.parameter);

  if (!enumOptions) {
    return (
      <Menu.Panel>
        <Menu.Section title="Loading options">
          <Menu.ItemButton>...</Menu.ItemButton>
        </Menu.Section>
      </Menu.Panel>
    );
  }

  return <ActualParamMenu {...props} enumOptions={enumOptions ?? []} />;
}

function ActualParamMenu(props: Props & { enumOptions: EnumOption[] }) {
  const { parameter, parameterValues: values, enumOptions, onChange } = props;

  const { isMultiselect = false, isFreeformAllowed = false } = parameter;

  const [activeTab, setActiveTab] = useState<"all" | "selected">("all");
  const [search, setSearch] = useState("");

  const [freeformOptions, setFreeformOptions] = useState<string[]>(() => {
    if (enumOptions.length >= 1_000_000) {
      return [];
    }
    return values.filter((value) => !enumOptions.includes(value));
  });

  const [
    VIRTUALIZED_MENU_WIDTH_PX,
    VIRTUALIZED_MENU_HEIGHT_PX,
    MENU_ITEM_HEIGHT_PX,
  ] = useMemo(
    () => [
      remToPx(VIRTUALIZED_MENU_WIDTH_REM),
      remToPx(VIRTUALIZED_MENU_HEIGHT_REM),
      remToPx(MENU_ITEM_HEIGHT_REM),
    ],
    []
  );

  const renderOption = (style?: CSSProperties) => (option: EnumOption) => {
    return (
      <Menu.ItemButton
        key={option}
        style={style}
        selected={values.includes(option)}
        onClick={() => {
          if (isMultiselect) {
            const wasSelected = values.includes(option);
            const nextValues = enumOptions
              .concat(freeformOptions)
              .filter((otherOption) =>
                otherOption === option
                  ? !wasSelected
                  : values.includes(otherOption)
              );

            onChange({
              ...parameter,
              values: nextValues,
            });

            if (wasSelected && freeformOptions.includes(option)) {
              setFreeformOptions((freeformOptions) =>
                freeformOptions.filter((other) => other !== option)
              );
            }
          } else {
            if (values.includes(option)) {
              return;
            }

            onChange({
              ...parameter,
              value: option,
            });

            setFreeformOptions([]);
          }
        }}
      >
        <Menu.Text>{option}</Menu.Text>
      </Menu.ItemButton>
    );
  };

  function renderMenuWithVirtualizedListAndSearchAndTabs() {
    function getOptions(activeTab: "all" | "selected") {
      function matchesSearch(option: EnumOption) {
        return option.toLowerCase().includes(search);
      }

      switch (activeTab) {
        case "all":
          return enumOptions.filter(matchesSearch);
        case "selected":
          return values.filter(matchesSearch);
      }
    }

    const options = getOptions(activeTab);

    function rowRenderer(props: {
      key: EnumOption;
      index: number;
      isScrolling: boolean;
      isVisible: boolean;
      style: CSSProperties;
    }) {
      const { index, style } = props;
      const option = options[index];

      return renderOption(style)(option);
    }

    return (
      <Menu.Panel className={styles.menuPanel}>
        {!isMultiselect && (
          <Menu.Section title="Selected">
            {values.map(renderOption())}
          </Menu.Section>
        )}
        <Menu.Section
          maxHeight={`${VIRTUALIZED_MENU_HEIGHT_REM}rem`}
          title={
            <div className={styles.menuSectionTitleWithSearchAndTabs}>
              <InputField
                size="M"
                style="contained"
                prefix={<IconMagnifyingGlass />}
                placeholder={isMultiselect ? "Search…" : "Search all…"}
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value.toLowerCase())
                }
              />
              {isMultiselect && (
                <TabList size="M" fillWidth marginBottom={false}>
                  <Tab
                    active={activeTab === "all"}
                    onClick={() => setActiveTab("all")}
                  >
                    All
                  </Tab>
                  <Tab
                    active={activeTab === "selected"}
                    onClick={() => setActiveTab("selected")}
                  >
                    Selected {values.length}
                  </Tab>
                </TabList>
              )}
            </div>
          }
        >
          <VirtualizedList
            width={VIRTUALIZED_MENU_WIDTH_PX}
            height={VIRTUALIZED_MENU_HEIGHT_PX}
            rowHeight={MENU_ITEM_HEIGHT_PX}
            rowCount={options.length}
            rowRenderer={rowRenderer}
          />
        </Menu.Section>

        {isFreeformAllowed && (
          <MenuSectionFreeformOptions
            parameter={parameter}
            parameterValues={values}
            onChange={onChange}
            enumOptions={enumOptions}
            freeformOptions={freeformOptions}
            setFreeformOptions={setFreeformOptions}
          />
        )}

        {activeTab === "selected" && (
          <Menu.Section>
            <Menu.ItemButton
              onClick={() => {
                if (!isMultiselect) {
                  // The "selected" tab, and this button, shouldn't be
                  // rendered unless the param isMultiselect.
                  return;
                }
                onChange({
                  ...parameter,
                  values: [],
                });
                setFreeformOptions([]);
              }}
            >
              Reset to default
            </Menu.ItemButton>
          </Menu.Section>
        )}
      </Menu.Panel>
    );
  }

  if (enumOptions.length > MAX_ITEMS_IN_PLAIN_MENU) {
    return renderMenuWithVirtualizedListAndSearchAndTabs();
  }

  return (
    <Menu.Panel className={styles.menuPanel}>
      <Menu.Section>{enumOptions.map(renderOption())}</Menu.Section>

      {isFreeformAllowed && (
        <MenuSectionFreeformOptions
          parameter={parameter}
          parameterValues={values}
          onChange={onChange}
          enumOptions={enumOptions}
          freeformOptions={freeformOptions}
          setFreeformOptions={setFreeformOptions}
        />
      )}
    </Menu.Panel>
  );
}

const MAX_ITEMS_IN_PLAIN_MENU = 10;

/*
 * This menu height fits on mobile*, and it makes it easy to see when
 * there are more items, since it shows half of the 10th item in the
 * section.
 *
 * *Screen must be at least 540px high, so won't fit in landscape mode
 * on mobile devices.
 */
const VIRTUALIZED_MENU_HEIGHT_REM = 30.5;

/*
 * Width of list within Menu.Section when Menu.Panel is at max width.
 */
const VIRTUALIZED_MENU_WIDTH_REM = 37.4;

/*
 * Height of Menu.ItemButton
 */
const MENU_ITEM_HEIGHT_REM = 3.2;
